/**
 * Metadados compartilhados de cada setor do portal Área do Cliente.
 * São usados pelo menu lateral, pelo dashboard, pelas páginas de setor e
 * pelo painel administrativo de upload.
 */

export const SECTORS = [
  "departamento_pessoal",
  "departamento_fiscal",
  "contabilidade",
  "paralegal",
  "financeiro",
] as const;

export type SectorKey = (typeof SECTORS)[number];

export type SectorMeta = {
  key: SectorKey;
  label: string;
  shortLabel: string;
  description: string;
  /** Tom semântico baseado nas cores da marca Só Facilitei. */
  tone: "orange" | "cyan" | "green" | "purple";
  /** Categorias sugeridas, baseadas na árvore enviada pelo cliente. */
  categories: string[];
  /** Caminho da página dedicada do setor. */
  path: string;
};

export const SECTOR_META: Record<SectorKey, SectorMeta> = {
  departamento_pessoal: {
    key: "departamento_pessoal",
    label: "Departamento Pessoal",
    shortLabel: "Dep. Pessoal",
    description: "Folha, holerites e guias trabalhistas",
    tone: "orange",
    categories: ["Folha de pagamento", "Holerite", "Guia trabalhista", "Outros"],
    path: "/portal/departamento-pessoal",
  },
  departamento_fiscal: {
    key: "departamento_fiscal",
    label: "Departamento Fiscal",
    shortLabel: "Dep. Fiscal",
    description: "Recibos, fechamentos e guias de impostos",
    tone: "cyan",
    categories: ["Recibo", "Fechamento fiscal", "Guia de imposto", "Outros"],
    path: "/portal/departamento-fiscal",
  },
  contabilidade: {
    key: "contabilidade",
    label: "Contabilidade",
    shortLabel: "Contabilidade",
    description: "Balanço Patrimonial, DRE e Balancete",
    tone: "orange",
    categories: ["Balanço Patrimonial", "DRE", "Balancete", "Outros"],
    path: "/portal/contabilidade",
  },
  paralegal: {
    key: "paralegal",
    label: "Paralegal",
    shortLabel: "Paralegal",
    description: "CNPJ, contratos e inscrições",
    tone: "cyan",
    categories: ["CNPJ", "Contrato Social", "Inscrição Estadual", "Inscrição Municipal", "Outros"],
    path: "/portal/paralegal",
  },
  financeiro: {
    key: "financeiro",
    label: "Financeiro",
    shortLabel: "Financeiro",
    description: "Recibos de pagamento e boletos",
    tone: "orange",
    categories: ["Recibo de pagamento", "Boleto", "Outros"],
    path: "/portal/financeiro",
  },
};

export const SECTOR_LIST: SectorMeta[] = SECTORS.map((key) => SECTOR_META[key]);

export function sectorToneClasses(tone: SectorMeta["tone"]) {
  switch (tone) {
    case "orange":
      return {
        chip: "bg-[var(--brand-orange)]/15 text-[var(--brand-orange)] border-[var(--brand-orange)]/20",
        ring: "ring-[var(--brand-orange)]/30",
        bar: "bg-[var(--brand-orange)]",
        text: "text-[var(--brand-orange)]",
      };
    case "cyan":
      return {
        chip: "bg-[var(--brand-cyan)]/15 text-[var(--brand-cyan)] border-[var(--brand-cyan)]/20",
        ring: "ring-[var(--brand-cyan)]/30",
        bar: "bg-[var(--brand-cyan)]",
        text: "text-[var(--brand-cyan)]",
      };
    case "green":
      return {
        chip: "bg-[var(--brand-green)]/15 text-[var(--brand-green)] border-[var(--brand-green)]/20",
        ring: "ring-[var(--brand-green)]/30",
        bar: "bg-[var(--brand-green)]",
        text: "text-[var(--brand-green)]",
      };
    case "purple":
      return {
        chip: "bg-[var(--brand-purple)]/15 text-[var(--brand-purple)] border-[var(--brand-purple)]/20",
        ring: "ring-[var(--brand-purple)]/30",
        bar: "bg-[var(--brand-purple)]",
        text: "text-[var(--brand-purple)]",
      };
  }
}

export function formatSectorLabel(key: string): string {
  return SECTOR_META[key as SectorKey]?.label ?? key;
}
